import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useI18n, type Lang } from "@/lib/i18n";
import { PageShell } from "@/components/site/PageShell";
import { CATEGORIES } from "@/lib/categories";
import {
  categoryBySlugQuery,
  storiesByCategoryQuery,
  formatLogDate,
  type StoryListItem,
} from "@/lib/stories";
import { queryOptions } from "@tanstack/react-query";

const emptyStoriesQuery = queryOptions({
  queryKey: ["stories", "category", "none"],
  queryFn: async (): Promise<StoryListItem[]> => [],
});

export const Route = createFileRoute("/$lang/categories/$slug")({
  head: ({ params }) => {
    const cat = CATEGORIES.find((c) => c.slug === params.slug);
    return {
      meta: [
        { title: `${cat?.label.en ?? "Category"} — MadCaptains` },
        { property: "og:url", content: `/${params.lang}/categories/${params.slug}` },
      ],
      links: [{ rel: "canonical", href: `/${params.lang}/categories/${params.slug}` }],
    };
  },
  loader: async ({ context, params }) => {
    const cat = CATEGORIES.find((c) => c.slug === params.slug);
    if (!cat) throw notFound();
    const row = await context.queryClient.ensureQueryData(categoryBySlugQuery(params.slug));
    if (row?.id) {
      await context.queryClient.ensureQueryData(
        storiesByCategoryQuery(row.id, params.lang as Lang),
      );
    }
    return { cat, categoryId: row?.id ?? null };
  },
  component: CategoryPage,
  errorComponent: ({ error }) => (
    <PageShell title="Could not load category">
      <p className="text-sm text-muted-foreground">{error.message}</p>
    </PageShell>
  ),
  notFoundComponent: () => null,
});

function CategoryPage() {
  const { cat, categoryId } = Route.useLoaderData();
  const { lang, t } = useI18n();
  const { data: stories } = useSuspenseQuery(
    categoryId ? storiesByCategoryQuery(categoryId, lang) : emptyStoriesQuery,
  );
  return (
    <PageShell
      eyebrow={`Signal flag · ${cat.flag.toUpperCase()}`}
      title={lang === "ru" ? cat.label.ru : cat.label.en}
      lead={t.page_stories_lead}
    >
      {stories.length === 0 ? (
        <div className="rounded-sm border border-dashed border-border bg-card/30 p-8 text-center">
          <p className="font-display italic text-muted-foreground">{t.page_stories_empty}</p>
          <Link
            to="/$lang/submit"
            params={{ lang }}
            className="mt-5 inline-flex rounded-full px-4 py-2 text-sm font-medium"
            style={{ background: "var(--amber)", color: "var(--ink)" }}
          >
            {t.nav_submit_cta}
          </Link>
        </div>
      ) : (
        <ol className="divide-y divide-border border-y border-border">
          {stories.map((s, i) => (
            <li key={s.id}>
              <Link
                to="/$lang/stories/$slug"
                params={{ lang, slug: s.slug }}
                className="group flex items-baseline gap-4 px-1 py-5 hover:bg-card/50"
              >
                <span className="w-12 text-right font-display text-xs text-muted-foreground tabular-nums">
                  {String(i + 1).padStart(3, "0")}
                </span>
                <span className="flex-1">
                  <span className="block font-display text-lg leading-snug">
                    {s.title ?? "Untitled entry"}
                  </span>
                  <span className="mt-1 block text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                    {formatLogDate(s.published_at, lang)}
                    {s.author_display ? ` · ${s.author_display}` : ""}
                  </span>
                </span>
                <span className="text-xs text-muted-foreground group-hover:text-foreground">→</span>
              </Link>
            </li>
          ))}
        </ol>
      )}
    </PageShell>
  );
}